import { DatePipe, DecimalPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject, Input, OnChanges } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { TranslatePipe } from '@ngx-translate/core'
import { ClientsService } from '../../clients.service'
import { ThresholdChipsInputComponent } from '../../common/threshold-chips-input/threshold-chips-input.component'
import { SubscriptionUsageResult } from '../../metrics.service'

/**
 * How full the bar reads. Usage past the allowance fills it and is called out in red rather than
 * overflowing the track, which would leave the bar looking the same at 101% as at 300%.
 */
export function usageBarFill(usedPercentage: number | null): number {
  return Math.min(Math.max(usedPercentage ?? 0, 0), 100)
}

export type UsageTone = 'normal' | 'approaching' | 'exceeded'

/** The point from which the bar stops reading as business as usual. */
const APPROACHING_PERCENTAGE = 80

export function usageTone(usedPercentage: number | null): UsageTone {
  if (usedPercentage === null) {
    return 'normal'
  }
  if (usedPercentage >= 100) {
    return 'exceeded'
  }
  return usedPercentage >= APPROACHING_PERCENTAGE ? 'approaching' : 'normal'
}

/**
 * How much of its subscription a client has used, and the thresholds at which its administrators are
 * emailed about it. Rendered only where support has recorded a contract.
 */
@Component({
  selector: 'app-subscription-usage-panel',
  standalone: true,
  imports: [DatePipe, DecimalPipe, FormsModule, ThresholdChipsInputComponent, TranslatePipe],
  templateUrl: './subscription-usage-panel.component.html',
  styleUrl: './subscription-usage-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SubscriptionUsagePanelComponent implements OnChanges {
  @Input({ required: true })
  usage!: SubscriptionUsageResult

  private clientsService = inject(ClientsService)
  private updateThresholdsMutation = this.clientsService.updateUsageAlertThresholds()

  thresholds: number[] = []
  isSaving = false

  // The saved thresholds are the state; the list is re-read whenever the panel is handed a fresh
  // answer so that a failed save does not leave a chip on screen the server never accepted.
  ngOnChanges() {
    this.thresholds = [...this.usage.alertThresholdPercentages]
  }

  get periodStart(): Date {
    return new Date(this.usage.periodStart)
  }

  /**
   * The period's last day. `periodEnd` is the first instant of the next one, so the day shown is the
   * one before it — a subscription that renews on 6 April runs to 5 April.
   */
  get periodEnd(): Date {
    return new Date(new Date(this.usage.periodEnd).getTime() - 24 * 60 * 60 * 1000)
  }

  /**
   * Negative once the allowance has been passed, which is what the panel calls out in red, and null
   * for a subscription with no allowance recorded, which has nothing left to run out.
   */
  get remainingHours(): number | null {
    return this.usage.allowanceHours === null ? null : this.usage.allowanceHours - this.usage.usedHours
  }

  get barFill(): number {
    return usageBarFill(this.usage.usedPercentage)
  }

  get tone(): UsageTone {
    return usageTone(this.usage.usedPercentage)
  }

  async saveThresholds(thresholds: number[]) {
    const previous = this.thresholds
    this.thresholds = thresholds
    this.isSaving = true
    try {
      await this.updateThresholdsMutation.mutateAsync(thresholds)
    } catch {
      // The toast comes from the mutation itself; here the list only has to go back to what is
      // actually stored, so the chips do not claim a change that was refused.
      this.thresholds = previous
    } finally {
      this.isSaving = false
    }
  }
}
